// /home/labdoo/zitech-nexus/frontend/src/pages/admin/NewsletterSettings.tsx
import React, { useState, useEffect } from 'react';
import { newsletterAPI } from '../../services/api';
import './NewsletterSettings.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Settings: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ns-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

const NewsletterSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    auto_confirm: true,
    welcome_email_enabled: true,
    weekly_digest_enabled: false,
    from_email: '',
    reply_to_email: '',
    footer_text: ''
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await newsletterAPI.getSettings();
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await newsletterAPI.updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="ns-loading-container">
        <div className="ns-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ns-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="ns-loading-text">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="ns-container">
      {/* Header */}
      <div className="ns-header">
        <div className="ns-header-left">
          <button className="ns-back-btn" onClick={() => window.history.back()}>
            <Icons.Back />
            Back
          </button>
          <h1 className="ns-title">
            <Icons.Settings />
            Newsletter Settings
          </h1>
          <p className="ns-subtitle">Configure your newsletter preferences and email settings</p>
        </div>
      </div>

      {/* Success Message */}
      {saved && (
        <div className="ns-success">
          <Icons.Check />
          <span>Settings saved successfully!</span>
        </div>
      )}

      {/* Settings Form */}
      <div className="ns-form">
        <div className="ns-form-grid">
          {/* Left Column */}
          <div className="ns-form-left">
            <div className="ns-card">
              <h2 className="ns-card-title">General Settings</h2>
              
              <div className="ns-setting-item">
                <label className="ns-checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.auto_confirm}
                    onChange={(e) => setSettings({...settings, auto_confirm: e.target.checked})}
                  />
                  <span className="ns-checkbox-text">Auto-confirm subscriptions (skip email confirmation)</span>
                </label>
              </div>

              <div className="ns-setting-item">
                <label className="ns-checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.welcome_email_enabled}
                    onChange={(e) => setSettings({...settings, welcome_email_enabled: e.target.checked})}
                  />
                  <span className="ns-checkbox-text">Send welcome email on subscription</span>
                </label>
              </div>

              <div className="ns-setting-item">
                <label className="ns-checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.weekly_digest_enabled}
                    onChange={(e) => setSettings({...settings, weekly_digest_enabled: e.target.checked})}
                  />
                  <span className="ns-checkbox-text">Send weekly digest newsletter</span>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="ns-form-right">
            <div className="ns-card">
              <h2 className="ns-card-title">Email Configuration</h2>
              
              <div className="ns-form-group">
                <label className="ns-form-label">From Email</label>
                <input
                  type="email"
                  className="ns-form-input"
                  value={settings.from_email}
                  onChange={(e) => setSettings({...settings, from_email: e.target.value})}
                  placeholder="newsletter@zitechsolutions.com"
                />
                <span className="ns-form-hint">The email address that will appear as the sender</span>
              </div>

              <div className="ns-form-group">
                <label className="ns-form-label">Reply-to Email</label>
                <input
                  type="email"
                  className="ns-form-input"
                  value={settings.reply_to_email}
                  onChange={(e) => setSettings({...settings, reply_to_email: e.target.value})}
                  placeholder="support@zitechsolutions.com"
                />
                <span className="ns-form-hint">The email address where replies will be sent</span>
              </div>

              <div className="ns-form-group">
                <label className="ns-form-label">Footer Text</label>
                <textarea
                  className="ns-form-textarea"
                  value={settings.footer_text}
                  onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                  rows={4}
                  placeholder="© 2024 ZiTech Solutions. All rights reserved."
                />
                <span className="ns-form-hint">This text will appear at the bottom of every newsletter</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="ns-form-actions">
          <button 
            className="ns-btn-primary" 
            onClick={handleSave} 
            disabled={loading}
          >
            {loading ? (
              <>
                <Icons.Spinner />
                Saving...
              </>
            ) : (
              <>
                <Icons.Save />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSettings;